import {
  ESTIMATION_DOCUMENT_KIND,
  REVISION_DOCUMENT_KIND,
  revisionDocumentUID,
} from "../../../../../utils/revisionUID";
import { wbsLabel } from "./treeLabels";

// A revision seeded before its module ever saved it has no documentUID of
// its own. Name it the way the module's own save would have
// ({PLANT}-{WBS}-R0) whenever the tree knows both names, so those rows
// read the same as saved ones instead of falling back to a bare "Rev 0".
const fallbackDocumentUID = (plantName, wbsName, kind, revision) =>
  plantName && wbsName
    ? revisionDocumentUID(plantName, wbsName, revision, kind)
    : `${kind === ESTIMATION_DOCUMENT_KIND ? "Estimation — " : ""}Rev ${revision}`;

const parseMaybeJson = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    return JSON.parse(value) ?? {};
  } catch {
    return {};
  }
};

// One entry per Estimation revision. Those are not separate project revision
// records: the Structure/Piping modules open the project's latest record and
// keep every Estimation revision as a numeric key of its `estimationTable`
// (Rev 0, 1, 2 ... all saved into that one record - see savePipingData /
// saveStructuralData). So the keys are what gets listed, not the records.
//
// Each key holds that revision's own documentUID, written by the module on
// save exactly as PV writes its Input Data one; `plantName` is only needed
// to name revisions saved before that existed (see fallbackDocumentUID).
export const extractProjectEstimationRevisions = (wbs, { plantName } = {}) => {
  const records = (Array.isArray(wbs?.revisions) ? wbs.revisions : [])
    .map((rev) => parseMaybeJson(rev?.data))
    .filter((data) => data && !data.__structuralProjectRevisionDeleted);
  // Same record the modules load (the last one), so the tree lists exactly
  // the revisions the page's Rev. dropdown will offer.
  const latest = records[records.length - 1];
  const table = parseMaybeJson(latest?.estimationTable);

  const wbsName = wbsLabel(wbs);

  return Object.keys(table)
    .filter((key) => key !== "" && !Number.isNaN(Number(key)))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => ({
      revision: key,
      documentUID:
        parseMaybeJson(table[key])?.documentUID ||
        fallbackDocumentUID(plantName, wbsName, REVISION_DOCUMENT_KIND, key),
    }));
};

// PV projects store their saved revisions as an object keyed by revision
// number (see PVContext.jsx's savePVData/loadPvData). Each revision carries
// its own Input Data documentUID. The nested "estimation" record does NOT
// carry its own documentUID (verified against live data - savePVData only
// ever writes DataGridView2/7/8, bomData, and cost/hours totals in there) -
// so "has estimation" is judged by whether the BOM grid actually has rows,
// and shown labeled by revision, the same way Piping/Structural's Estimation
// entries are (they have no per-item document identifier either).
export const extractPvRevisionEntries = (
  allDataRes,
  { plantName, wbsName } = {},
) => {
  const bucket = allDataRes?.all?.inputData;
  if (!bucket || typeof bucket !== "object") return [];

  return Object.entries(bucket)
    .filter(([key]) => !isNaN(Number(key)))
    .map(([revision, entry]) => {
      const estimation = entry?.estimation;
      const hasEstimationData =
        Array.isArray(estimation?.DataGridView8) &&
        estimation.DataGridView8.length > 0;

      return {
        revision,
        documentUID:
          entry?.documentUID ||
          fallbackDocumentUID(plantName, wbsName, REVISION_DOCUMENT_KIND, revision),
        estimationDocumentUID: hasEstimationData
          ? estimation?.documentUID ||
            fallbackDocumentUID(plantName, wbsName, ESTIMATION_DOCUMENT_KIND, revision)
          : null,
      };
    })
    .sort((a, b) => Number(a.revision) - Number(b.revision));
};
